import React, { useMemo, useCallback, useRef } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import ListingCard from '../ListingCard';
import { CommercialPropertyBoundaryItem } from '@zoocasa/go-search';
import CircularProgress from '@mui/material/CircularProgress';
import type { RegularBreakpoints } from '@mui/material/Grid';
import type { SxProps } from '@mui/material';

export type ListingGridProps = {
  listings: CommercialPropertyBoundaryItem[];
  viewType?: 'map' | 'grid';
  onLoadMore?: () => void;
  hasMore: boolean;
  isLoadingMore?: boolean;
  /** Optional sx for the scroll container */
  containerSx?: SxProps;
};

export default function ListingGrid({ listings, viewType = 'map', onLoadMore, hasMore = true, isLoadingMore = false, containerSx }: ListingGridProps) {
  const gridColumns = useMemo<RegularBreakpoints>(() => viewType === 'map' ? {
    xs: 12,
    lg: 6,
  } : {
    xs: 10,
    sm: 5,
    md: 2.5,
    lg: 2,
  }, [viewType]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastScrollTopRef = useRef<number>(0);
  const lastFireRef = useRef<number>(0);

  const handleScroll = useCallback(() => {
    if (!hasMore || isLoadingMore) return;
    const el = scrollRef.current;
    if (!el) return;
    const currentTop = el.scrollTop;
    // Only trigger when scrolling down
    if (currentTop < lastScrollTopRef.current) {
      lastScrollTopRef.current = currentTop;
      return;
    }
    lastScrollTopRef.current = currentTop;
    const threshold = 200; // px before bottom
    const now = Date.now();
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      // Basic cooldown to avoid rapid re-fires while hovering near bottom
      if (now - lastFireRef.current < 800) return;
      lastFireRef.current = now;
      onLoadMore?.();
    }
  }, [hasMore, isLoadingMore, onLoadMore]);

  if (isLoadingMore && !listings.length) {
    return (
      <Grid item xs container justifyContent='center' alignItems='center' height='-webkit-fill-available'>
        <CircularProgress size={100} />
      </Grid>
    );
  }

  const content = (
    <Grid container spacing={2} columns={viewType === 'map' ? 12 : 10}>
      {listings.map(l => (
        <Grid key={l.slug} item {...gridColumns}>
          <ListingCard {...l} />
        </Grid>
      ))}
      {isLoadingMore && hasMore && (
        <Grid item xs={12} container justifyContent='center' alignItems='center'>
          <CircularProgress size={100} />
        </Grid>
      )}
    </Grid>
  );

  return (
    <Box ref={scrollRef} sx={containerSx} onScroll={handleScroll}>
      {content}
    </Box>
  );
}


